/**
 * Inspection Queue - OC Inspections scheduling queue (Chunk C / Gap 5).
 *
 * The Coordinator's contractor-first staging surface. The unit of work
 * is ONE installing contractor at a time - never a flat 65k registration
 * list. Two tabs:
 *
 *   - "Pool": pick a sub-type to stage as, pick a contractor (sorted so
 *     those behind on their Inspection Matrix quota float to the top),
 *     then page through that contractor's eligible homes and stage a
 *     selection into DRAFT inspections.
 *   - "Staged queue": the draft + unscheduled inspections already staged,
 *     the coordinator's shortlist headed for the Scheduling Console.
 *
 * Every option list (sub-types) comes from the API; nothing tenant- or
 * sub-type-specific is hardcoded here.
 */

import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import {
  CalendarClock,
  ClipboardCheck,
  Layers,
  Loader2,
  MapPin,
  Navigation,
  Sparkles,
  Upload,
  Users,
} from 'lucide-react'
import { AppShell } from '@/admin/components/app-shell'
import {
  DataTable,
  type DataTableColumn,
} from '@/admin/components/data-table'
import { BulkImportDialog } from '@/admin/components/imports/bulk-import-dialog'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { SearchInput } from '@/shared/components/ui/search-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { usePartnerships } from '@/admin/hooks/use-partnerships'
import { usePermissions } from '@/admin/hooks/use-permissions'
import {
  useAutoStageRegistrations,
  useContractorQuotas,
  useContractorSummary,
  useInspectionPoolFilterOptions,
  useInspectionSubTypes,
  usePoolRegistrations,
  useStageRegistrations,
  type ContractorQuota,
  type PoolRegistration,
} from '@/admin/hooks/use-inspections'
import type { Partnership } from '@/admin/types/organisations'
import { ContractorSummaryTable } from './contractor-summary-table'
import { ItinerariesTab } from './itineraries-tab'
import { SmartScheduleDialog } from './smart-schedule-dialog'

const DEFAULT_PAGE_SIZE = 25
// Sentinel for the "no filter" option in a single-select attribute filter.
const ANY_VALUE = '__any'
// Generic re-inspection cadence presets (months due). A numeric threshold
// control, not tenant data - like a date filter.
const CYCLE_WINDOW_OPTIONS = [12, 18, 24, 36]

type QueueTab = 'pool' | 'itineraries'

/** One installing contractor joined to its inspection-matrix quota. */
interface ContractorRow {
  uuid: string
  name: string
  quota: ContractorQuota | undefined
}

/** Compact one-line property address, omitting empty parts. */
function poolAddress(reg: PoolRegistration): string {
  const a = reg.property_address
  if (!a) return '-'
  const parts = [a.line_1, a.city, a.postal_code].filter(Boolean)
  return parts.length ? parts.join(', ') : '-'
}

/**
 * Merge two facet lists into one option list, primary winning on collision.
 * Used for the Pool toolbar's geography pickers: the prior-year registration
 * book is the primary signal, topped up with areas that only have staged work
 * so far, so a coordinator can still scope to them.
 */
function unionFacets<T extends { count: number }>(
  primary: T[] | undefined,
  secondary: T[] | undefined,
  keyOf: (row: T) => string,
): T[] {
  const byKey = new Map<string, T>()
  for (const row of secondary ?? []) if (keyOf(row)) byKey.set(keyOf(row), row)
  for (const row of primary ?? []) if (keyOf(row)) byKey.set(keyOf(row), row)
  return [...byKey.values()].sort((a, b) => b.count - a.count)
}

export function InspectionQueuePage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { can } = usePermissions()
  const bulkImportsEnabled = useFeatureFlag(FLAGS.BULK_IMPORTS)
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false)
  // Staging creates inspections, so it needs `inspections.create`. Without
  // it the pool is read-only (no select column, no stage button) - the
  // backend enforces the same gate, this keeps the UI honest.
  const canStage = can('inspections.create')
  // ?tab=itineraries deep-links straight to the trips surface (e.g. the
  // Itineraries button on the Inspections landing page).
  const [tab, setTab] = useState<QueueTab>(() =>
    new URLSearchParams(window.location.search).get('tab') === 'itineraries'
      ? 'itineraries'
      : 'pool',
  )
  // Smart Schedule scope - contractor org uuids ticked in the community table.
  const [selectedContractors, setSelectedContractors] = useState<Set<string>>(
    () => new Set(),
  )
  const [smartOpen, setSmartOpen] = useState(false)
  // Planning knobs: scope the community numbers geographically (state,
  // service area, city, country - they cascade), and/or override the matrix
  // percentage (due = ceil(pct x regs)).
  const [poolState, setPoolState] = useState('')
  const [poolCity, setPoolCity] = useState('')
  const [poolCountry, setPoolCountry] = useState('')
  const [poolZone, setPoolZone] = useState('')
  const [targetPct, setTargetPct] = useState('')
  const { data: contractorSummary, isLoading: summaryLoading } =
    useContractorSummary({
      state: poolState || undefined,
      city: poolCity || undefined,
      country: poolCountry || undefined,
      zone: poolZone || undefined,
      targetPct: targetPct || undefined,
    })
  // The option lists are facets over the whole book, NOT over the current
  // scope - otherwise picking a city would empty the city picker and strand
  // the user with no way back.
  //
  // Each list is the union of two facets: the prior-year registration book
  // (which is what the Registrations / Due / Done columns are derived from)
  // and the staged pool. An area with staged work but no book yet is still a
  // scope worth offering, and the count shown is the book's where there is
  // one, otherwise how much staged work sits there.
  const cityFilterOptions = useMemo(
    () =>
      unionFacets(
        contractorSummary?.registration_cities,
        contractorSummary?.candidates_by_city,
        (c) => c.city,
      ),
    [contractorSummary],
  )
  const countryFilterOptions = useMemo(
    () =>
      unionFacets(
        contractorSummary?.registration_countries,
        contractorSummary?.candidates_by_country,
        (c) => c.country,
      ),
    [contractorSummary],
  )
  const zoneFilterOptions = useMemo(
    () =>
      unionFacets(
        contractorSummary?.registration_zones,
        contractorSummary?.candidates_by_zone,
        (z) => z.zone,
      ),
    [contractorSummary],
  )

  // ── Sub-type picker (drives the already_staged flag + staging) ──────
  const { data: subTypes } = useInspectionSubTypes()
  // You stage a registration/holding, so only holding-bound sub-types
  // are valid staging targets.
  const stageableSubTypes = useMemo(
    () => (subTypes ?? []).filter((st) => st.requires_holding),
    [subTypes],
  )
  const [subTypeKey, setSubTypeKey] = useState<string>('')
  // Default to the first stageable sub-type once the catalogue loads.
  const effectiveSubTypeKey =
    subTypeKey || stageableSubTypes[0]?.key || ''

  // ── Contractor list (partnerships joined to quotas) ─────────────────
  const { data: partnershipsData } = usePartnerships({ page_size: '200' })
  const contractorUuids = useMemo(
    () =>
      (partnershipsData?.results ?? [])
        .map((p: Partnership) => p.downstream_organisation?.uuid)
        .filter((u): u is string => !!u),
    [partnershipsData],
  )
  const { data: quotas } = useContractorQuotas(contractorUuids)
  const quotaByContractor = useMemo(() => {
    const map = new Map<string, ContractorQuota>()
    for (const q of quotas ?? []) map.set(q.contractor_uuid, q)
    return map
  }, [quotas])


  const [selectedContractor, setSelectedContractor] =
    useState<ContractorRow | null>(null)

  // ── Pool (eligible homes for the selected contractor) ───────────────
  const [poolSearch, setPoolSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [hideOpenClaims, setHideOpenClaims] = useState(true)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [selectedHoldings, setSelectedHoldings] = useState<Set<string>>(
    () => new Set(),
  )

  // ── Eligibility filters + proximity (Chunk C2) ──────────────────────
  const { data: filterAttrs } = useInspectionPoolFilterOptions()
  // One value per attribute code (single-select); empty => no filter.
  const [attributeFilters, setAttributeFilters] = useState<
    Record<string, string[]>
  >({})
  const [cycleMonths, setCycleMonths] = useState<number | undefined>(undefined)
  const [paidWarrantyOnly, setPaidWarrantyOnly] = useState(false)
  const [groupByZone, setGroupByZone] = useState(false)
  const [closestN, setClosestN] = useState(10)
  const autoStage = useAutoStageRegistrations()

  // Eligibility filters shared by the pool query and the auto-stage actions,
  // so a "closest N" / cycle-list batch is drawn from what the coordinator
  // sees.
  const eligibilityFilters = useMemo(
    () => ({
      attribute_filters: attributeFilters,
      cycle_months: cycleMonths ?? null,
      exclude_with_open_claims: hideOpenClaims,
      has_paid_warranty: paidWarrantyOnly ? true : null,
    }),
    [attributeFilters, cycleMonths, hideOpenClaims, paidWarrantyOnly],
  )

  function setAttributeFilter(code: string, value: string) {
    setAttributeFilters((prev) => {
      const next = { ...prev }
      if (value === ANY_VALUE) delete next[code]
      else next[code] = [value]
      return next
    })
    setPage(1)
  }

  // Debounce the search term so typing doesn't fire a pool query (~65k
  // rows) on every keystroke; reset to page 1 once the term settles.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(poolSearch)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [poolSearch])

  const poolParams = useMemo(
    () => ({
      contractor: selectedContractor?.uuid,
      sub_type: effectiveSubTypeKey || undefined,
      search: debouncedSearch || undefined,
      exclude_with_open_claims: hideOpenClaims ? 'true' : undefined,
      has_paid_warranty: paidWarrantyOnly ? 'true' : undefined,
      cycle_months: cycleMonths,
      attributeFilters,
      ordering: groupByZone ? 'property_zone_code' : undefined,
      page,
      page_size: pageSize,
    }),
    [
      selectedContractor,
      effectiveSubTypeKey,
      debouncedSearch,
      hideOpenClaims,
      paidWarrantyOnly,
      cycleMonths,
      attributeFilters,
      groupByZone,
      page,
      pageSize,
    ],
  )
  const {
    data: pool,
    isLoading: poolLoading,
    isFetching: poolFetching,
  } = usePoolRegistrations(poolParams, !!selectedContractor)
  const poolResults = pool?.results ?? []
  const poolCount = pool?.count ?? 0

  const stage = useStageRegistrations()

  // ── Handlers ────────────────────────────────────────────────────────
  function selectContractor(row: ContractorRow) {
    setSelectedContractor(row)
    setSelectedHoldings(new Set())
    setPage(1)
    setPoolSearch('')
    // Default "stage closest" to what this contractor still needs.
    const remaining = row.quota?.inspections_remaining ?? 0
    setClosestN(remaining > 0 ? remaining : 10)
  }

  function toggleHolding(uuid: string, checked: boolean) {
    setSelectedHoldings((prev) => {
      const next = new Set(prev)
      if (checked) next.add(uuid)
      else next.delete(uuid)
      return next
    })
  }

  const allOnPageSelected =
    poolResults.length > 0 &&
    poolResults.every((r) => selectedHoldings.has(r.holding_uuid))

  function toggleAllOnPage(checked: boolean) {
    setSelectedHoldings((prev) => {
      const next = new Set(prev)
      for (const r of poolResults) {
        if (checked) next.add(r.holding_uuid)
        else next.delete(r.holding_uuid)
      }
      return next
    })
  }

  async function stageSelected() {
    if (!effectiveSubTypeKey || selectedHoldings.size === 0) return
    try {
      const result = await stage.mutateAsync({
        holding_uuids: Array.from(selectedHoldings),
        sub_type_key: effectiveSubTypeKey,
        contractor_uuid: selectedContractor?.uuid,
      })
      const { staged, skipped, errors } = result.summary
      const bits = [`Staged ${staged}`]
      if (skipped) bits.push(`skipped ${skipped}`)
      if (errors) bits.push(`${errors} error${errors === 1 ? '' : 's'}`)
      const message = bits.join(', ')
      if (errors) toast.error(message)
      else toast.success(message)
      setSelectedHoldings(new Set())
    } catch {
      toast.error('Could not stage the selected registrations.')
    }
  }

  function summariseStage(summary: {
    staged: number
    skipped: number
    errors: number
  }): string {
    const bits = [`Staged ${summary.staged}`]
    if (summary.skipped) bits.push(`skipped ${summary.skipped}`)
    if (summary.errors)
      bits.push(`${summary.errors} error${summary.errors === 1 ? '' : 's'}`)
    return bits.join(', ')
  }

  async function stageClosest() {
    if (!effectiveSubTypeKey || !selectedContractor?.uuid || closestN < 1) return
    try {
      const result = await autoStage.mutateAsync({
        mode: 'contractor',
        sub_type_key: effectiveSubTypeKey,
        contractor_uuid: selectedContractor.uuid,
        limit: closestN,
        ...eligibilityFilters,
      })
      const message = summariseStage(result.summary)
      if (result.summary.errors) toast.error(message)
      else toast.success(message)
      setSelectedHoldings(new Set())
    } catch {
      toast.error('Could not stage the closest homes.')
    }
  }


  // ── Columns ─────────────────────────────────────────────────────────

  const poolColumns: DataTableColumn<PoolRegistration>[] = useMemo(() => {
    const cols: DataTableColumn<PoolRegistration>[] = []
    if (canStage) {
      cols.push({
        id: 'select',
        header: '',
        cell: (row) => (
          <Checkbox
            checked={selectedHoldings.has(row.holding_uuid)}
            onCheckedChange={(checked) =>
              toggleHolding(row.holding_uuid, checked === true)
            }
            aria-label={`Select ${row.registration_reference}`}
          />
        ),
      })
    }
    cols.push(
      {
        id: 'registration_reference',
        header: 'Reference',
        cell: (row) => (
          <span className="font-mono text-[11px] uppercase tracking-[0.02em] text-[var(--ink-2)]">
            {row.registration_reference || '-'}
          </span>
        ),
      },
      {
        id: 'customer_name',
        header: 'Customer',
        cell: (row) => (
          <span className="text-[12px] text-[var(--ink)]">
            {row.customer_name || '-'}
          </span>
        ),
      },
      {
        id: 'property',
        header: 'Property',
        cell: (row) => (
          <span
            className="block max-w-[220px] truncate text-[11px] text-[var(--ink-3)]"
            title={poolAddress(row)}
          >
            {poolAddress(row)}
          </span>
        ),
      },
      {
        id: 'area',
        header: 'Area',
        cell: (row) =>
          row.property_zone_name ? (
            <Badge variant="outline">{row.property_zone_name}</Badge>
          ) : (
            <span className="text-[11px] text-[var(--ink-3)]">-</span>
          ),
      },
      {
        id: 'product_name',
        header: 'Product',
        cell: (row) => (
          <span
            className="block max-w-[180px] truncate text-[12px] text-[var(--ink-2)]"
            title={row.product_name || undefined}
          >
            {row.product_name || '-'}
          </span>
        ),
      },
      {
        id: 'open_claims',
        header: 'Open claims',
        cell: (row) =>
          row.open_claim_count > 0 ? (
            <Badge variant="destructive">{row.open_claim_count}</Badge>
          ) : (
            <span className="text-[11px] text-[var(--ink-3)]">-</span>
          ),
      },
    )
    return cols
  }, [selectedHoldings, canStage])


  return (
    <AppShell>
      <main className="page-container py-10">
        <header className="flex flex-col gap-4">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h1 className="font-display text-[42px] leading-[1.1] text-[var(--ink)]">
                Scheduling Queue
              </h1>
              <p className="mt-1 text-sm text-[var(--ink-3)]">
                Stage eligible homes per contractor, then schedule them on
                the console.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/inspections')}
              >
                <ClipboardCheck size={14} className="mr-1.5" />
                List view
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/inspections/scheduling')}
              >
                <CalendarClock size={14} className="mr-1.5" />
                Scheduling Console
              </Button>
            </div>
          </div>

          <Tabs value={tab} onValueChange={(v) => setTab(v as QueueTab)}>
            <TabsList variant="line">
              <TabsTrigger value="pool">
                <Layers size={13} className="mr-1.5" />
                Pool
              </TabsTrigger>
              <TabsTrigger value="itineraries">
                <CalendarClock size={13} className="mr-1.5" />
                Itineraries
              </TabsTrigger>
            </TabsList>

            {/* ── Pool tab ───────────────────────────────────────── */}
            <TabsContent value="pool">
              <div className="mt-6 flex flex-col gap-6">
                {/* Sub-type picker */}
                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] px-4 py-3">
                  <span className="text-[12px] font-medium text-[var(--ink-2)]">
                    Stage as
                  </span>
                  <Select
                    value={effectiveSubTypeKey}
                    onValueChange={(v) => {
                      setSubTypeKey(v)
                      setSelectedHoldings(new Set())
                      setPage(1)
                    }}
                  >
                    <SelectTrigger className="h-9 min-w-[240px]">
                      <SelectValue placeholder="Select a sub-type" />
                    </SelectTrigger>
                    <SelectContent>
                      {stageableSubTypes.map((st) => (
                        <SelectItem key={st.key} value={st.key}>
                          {st.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {stageableSubTypes.length === 0 && (
                    <span className="text-[11px] text-[var(--ink-3)]">
                      No holding-bound sub-types are configured yet.
                    </span>
                  )}
                  <span className="ml-2 text-[12px] font-medium text-[var(--ink-2)]">
                    State
                  </span>
                  <Select
                    value={poolState || 'all'}
                    onValueChange={(v: string) =>
                      setPoolState(v === 'all' ? '' : v)
                    }
                  >
                    <SelectTrigger className="h-9 w-[150px]">
                      <SelectValue placeholder="All states" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All states</SelectItem>
                      {(contractorSummary?.registration_states ?? [])
                        .filter((s) => s.state)
                        .map((s) => (
                          <SelectItem key={s.state} value={s.state}>
                            {s.state} · {s.count}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {/* Service area / city / country narrow the same numbers
                      the State select does. Each is hidden when the book
                      holds no values for it, so a single-country tenant
                      never sees a one-option Country picker. */}
                  {zoneFilterOptions.length > 0 && (
                    <>
                      <span className="text-[12px] font-medium text-[var(--ink-2)]">
                        Service area
                      </span>
                      <Select
                        value={poolZone || 'all'}
                        onValueChange={(v: string) =>
                          setPoolZone(v === 'all' ? '' : v)
                        }
                      >
                        <SelectTrigger className="h-9 w-[170px]">
                          <SelectValue placeholder="All service areas" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All service areas</SelectItem>
                          {zoneFilterOptions.map((z) => (
                            <SelectItem key={z.zone} value={z.zone}>
                              {z.label || z.zone} · {z.count}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                  {cityFilterOptions.length > 0 && (
                    <>
                      <span className="text-[12px] font-medium text-[var(--ink-2)]">
                        City
                      </span>
                      <Select
                        value={poolCity || 'all'}
                        onValueChange={(v: string) =>
                          setPoolCity(v === 'all' ? '' : v)
                        }
                      >
                        <SelectTrigger className="h-9 w-[170px]">
                          <SelectValue placeholder="All cities" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All cities</SelectItem>
                          {cityFilterOptions.map((c) => (
                            <SelectItem key={c.city} value={c.city}>
                              {c.city} · {c.count}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                  {countryFilterOptions.length > 1 && (
                    <>
                      <span className="text-[12px] font-medium text-[var(--ink-2)]">
                        Country
                      </span>
                      <Select
                        value={poolCountry || 'all'}
                        onValueChange={(v: string) =>
                          setPoolCountry(v === 'all' ? '' : v)
                        }
                      >
                        <SelectTrigger className="h-9 w-[150px]">
                          <SelectValue placeholder="All countries" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All countries</SelectItem>
                          {countryFilterOptions.map((c) => (
                            <SelectItem key={c.country} value={c.country}>
                              {c.country} · {c.count}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                  <span className="text-[12px] font-medium text-[var(--ink-2)]">
                    Inspections %
                  </span>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    placeholder="SOP"
                    title="Override the Inspection Matrix percentage - due = this % of prior-year paid registrations. Blank = the SOP tier."
                    value={targetPct}
                    onChange={(e) => setTargetPct(e.target.value)}
                    className="h-9 w-[80px]"
                  />
                  {canStage && bulkImportsEnabled && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!effectiveSubTypeKey}
                      onClick={() => setBulkUploadOpen(true)}
                      className="ml-auto"
                    >
                      <Upload size={14} className="mr-1.5" />
                      Bulk upload
                    </Button>
                  )}
                </div>

                {/* Contractor list */}
                <section className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Users size={15} className="text-[var(--ink-3)]" />
                      <h2 className="text-[14px] font-medium text-[var(--ink)]">
                        Contractors
                      </h2>
                      <span className="text-[11px] text-[var(--ink-3)]">
                        {(contractorSummary?.results ?? []).length} groups -
                        behind schedule first
                        {selectedContractors.size > 0 &&
                          ` - ${selectedContractors.size} selected`}
                      </span>
                    </div>
                    {canStage && (
                      <Button
                        size="sm"
                        disabled={!effectiveSubTypeKey}
                        onClick={() => setSmartOpen(true)}
                        className="bg-[var(--green,#1c5a48)] text-white hover:bg-[var(--green,#1c5a48)]/90"
                      >
                        <Sparkles size={14} className="mr-1.5" />
                        Smart Schedule
                      </Button>
                    )}
                  </div>
                  <ContractorSummaryTable
                    summary={contractorSummary}
                    isLoading={summaryLoading}
                    selected={selectedContractors}
                    onToggle={(uuids, next) =>
                      setSelectedContractors((prev) => {
                        const nextSet = new Set(prev)
                        for (const u of uuids) {
                          if (next) nextSet.add(u)
                          else nextSet.delete(u)
                        }
                        return nextSet
                      })
                    }
                    onToggleAll={(next) =>
                      setSelectedContractors(() => {
                        if (!next) return new Set()
                        const all = new Set<string>()
                        for (const g of contractorSummary?.results ?? []) {
                          all.add(g.group.organisation_uuid)
                          for (const c of g.children)
                            all.add(c.organisation_uuid)
                        }
                        return all
                      })
                    }
                    onOpenContractor={(uuid, name) =>
                      selectContractor({
                        uuid,
                        name,
                        quota: quotaByContractor.get(uuid),
                      })
                    }
                  />
                </section>

                {/* Eligible homes for the selected contractor */}
                {selectedContractor ? (
                  <section className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <MapPin size={15} className="text-[var(--ink-3)]" />
                        <h2 className="text-[14px] font-medium text-[var(--ink)]">
                          {selectedContractor.name}
                        </h2>
                        <span className="text-[11px] text-[var(--ink-3)]">
                          {poolCount} eligible home{poolCount === 1 ? '' : 's'}
                        </span>
                      </div>
                      {canStage && (
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="flex items-center gap-1.5 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] px-2 py-1">
                            <Input
                              type="number"
                              min={1}
                              value={closestN}
                              onChange={(e) =>
                                setClosestN(
                                  Math.max(1, parseInt(e.target.value, 10) || 1),
                                )
                              }
                              aria-label="Number of closest homes to stage"
                              className="h-8 w-16"
                            />
                            <Button
                              variant="outline"
                              disabled={!effectiveSubTypeKey || autoStage.isPending}
                              onClick={stageClosest}
                            >
                              {autoStage.isPending ? (
                                <Loader2 size={14} className="mr-1.5 animate-spin" />
                              ) : (
                                <Navigation size={14} className="mr-1.5" />
                              )}
                              Stage closest
                            </Button>
                          </div>
                          <Button
                            disabled={
                              selectedHoldings.size === 0 ||
                              !effectiveSubTypeKey ||
                              stage.isPending
                            }
                            onClick={stageSelected}
                          >
                            {stage.isPending ? (
                              <Loader2 size={14} className="mr-1.5 animate-spin" />
                            ) : (
                              <Layers size={14} className="mr-1.5" />
                            )}
                            Stage selected ({selectedHoldings.size})
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <SearchInput
                        value={poolSearch}
                        onChange={setPoolSearch}
                        placeholder="Search homes..."
                        className="w-[240px]"
                      />
                      {/* Generic registration-attribute filters (roof pitch,
                          stories, ...) - options served from the API. */}
                      {(filterAttrs ?? []).map((attr) => (
                        <Select
                          key={attr.code_name}
                          value={attributeFilters[attr.code_name]?.[0] ?? ANY_VALUE}
                          onValueChange={(v) =>
                            setAttributeFilter(attr.code_name, v)
                          }
                        >
                          <SelectTrigger className="h-9 min-w-[160px]">
                            <SelectValue placeholder={attr.label} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={ANY_VALUE}>
                              Any {attr.label.toLowerCase()}
                            </SelectItem>
                            {attr.options.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ))}
                      {/* Re-inspection cycle window. */}
                      <Select
                        value={cycleMonths ? String(cycleMonths) : ANY_VALUE}
                        onValueChange={(v) => {
                          setCycleMonths(v === ANY_VALUE ? undefined : Number(v))
                          setPage(1)
                        }}
                      >
                        <SelectTrigger className="h-9 min-w-[160px]">
                          <SelectValue placeholder="Re-inspection cycle" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ANY_VALUE}>Any cycle</SelectItem>
                          {CYCLE_WINDOW_OPTIONS.map((m) => (
                            <SelectItem key={m} value={String(m)}>
                              Due after {m} months
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Label className="cursor-pointer text-[12px] font-normal text-[var(--ink-2)]">
                        <Checkbox
                          checked={hideOpenClaims}
                          onCheckedChange={(checked) => {
                            setHideOpenClaims(checked === true)
                            setPage(1)
                          }}
                          aria-label="Hide registrations with open claims"
                        />
                        Hide open claims
                      </Label>
                      <Label className="cursor-pointer text-[12px] font-normal text-[var(--ink-2)]">
                        <Checkbox
                          checked={paidWarrantyOnly}
                          onCheckedChange={(checked) => {
                            setPaidWarrantyOnly(checked === true)
                            setPage(1)
                          }}
                          aria-label="Only paid warranties"
                        />
                        Paid warranty only
                      </Label>
                      <Label className="cursor-pointer text-[12px] font-normal text-[var(--ink-2)]">
                        <Checkbox
                          checked={groupByZone}
                          onCheckedChange={(checked) => {
                            setGroupByZone(checked === true)
                            setPage(1)
                          }}
                          aria-label="Group homes by area"
                        />
                        Group by area
                      </Label>
                      {canStage && poolResults.length > 0 && (
                        <Label className="cursor-pointer text-[12px] font-normal text-[var(--ink-2)]">
                          <Checkbox
                            checked={allOnPageSelected}
                            onCheckedChange={(checked) =>
                              toggleAllOnPage(checked === true)
                            }
                            aria-label="Select all homes on this page"
                          />
                          Select page
                        </Label>
                      )}
                    </div>

                    <DataTable<PoolRegistration>
                      tableId="inspection-queue-pool"
                      columns={poolColumns}
                      data={poolResults}
                      isLoading={poolLoading}
                      isFetching={poolFetching}
                      rowKey="holding_uuid"
                      page={page}
                      pageSize={pageSize}
                      totalCount={poolCount}
                      onPageChange={setPage}
                      onPageSizeChange={(size) => {
                        setPageSize(size)
                        setPage(1)
                      }}
                      emptyStateNode={
                        <EmptyState
                          icon={MapPin}
                          title="No eligible homes"
                          description="No registrations match the current filters for this contractor."
                        />
                      }
                    />
                  </section>
                ) : (
                  <div className="rounded-lg border border-dashed border-[var(--rule)] bg-[var(--bg)] px-4 py-10 text-center text-[13px] text-[var(--ink-3)]">
                    Select a contractor above to see their eligible homes.
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ── Itineraries tab ────────────────────────────────── */}
            <TabsContent value="itineraries">
              <div className="mt-6">
                <ItinerariesTab
                  onSmartSchedule={() => setSmartOpen(true)}
                  canSchedule={canStage}
                />
              </div>
            </TabsContent>
          </Tabs>
        </header>
      </main>

      {effectiveSubTypeKey && smartOpen && (
        <SmartScheduleDialog
          open={smartOpen}
          onOpenChange={setSmartOpen}
          subTypeKey={effectiveSubTypeKey}
          selectedContractorUuids={Array.from(selectedContractors)}
          summary={contractorSummary}
          initialState={poolState}
          initialCity={poolCity}
          initialCountry={poolCountry}
          initialZone={poolZone}
        />
      )}

      {bulkImportsEnabled && effectiveSubTypeKey && (
        <BulkImportDialog
          open={bulkUploadOpen}
          onOpenChange={setBulkUploadOpen}
          target={`inspection_staging:${effectiveSubTypeKey}`}
          title="Bulk upload homes to stage"
          onComplete={() => {
            qc.invalidateQueries({ queryKey: ['inspections'] })
            qc.invalidateQueries({ queryKey: ['inspection-pool'] })
            qc.invalidateQueries({ queryKey: ['inspection-contractor-quotas'] })
          }}
        />
      )}
    </AppShell>
  )
}
